import { jsPDF } from "jspdf";
import type { SchoolInfo } from "@/types";
import type { Certificate } from "@/services/certificateService";
import { drawImageContain, loadPdfImage, pdfDate } from "./pdfImages";

const GREEN: [number, number, number] = [29, 95, 208];
const TEXT: [number, number, number] = [40, 40, 38];
const MUTED: [number, number, number] = [110, 110, 106];

/**
 * Certificat de scolarité (A4 portrait). Les informations de l'élève et le signataire sont celles figées
 * à la délivrance : une réimpression donne le même document.
 */
export async function exportCertificatePdf(certificate: Certificate, school: SchoolInfo): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 22;
  const width = pageW - margin * 2;
  const logo = await loadPdfImage(school.logo);

  // En-tête de l'école.
  let headerX = margin;
  if (logo) {
    drawImageContain(doc, logo, margin, 18, 24, 24);
    headerX = margin + 30;
  }
  doc.setTextColor(...GREEN);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(doc.splitTextToSize(school.name.toUpperCase(), pageW - margin - headerX), headerX, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const lines = [
    [school.address, school.city, school.country].filter(Boolean).join(", "),
    [school.phone && `Tél. : ${school.phone}`, school.email].filter(Boolean).join("  ·  "),
    school.slogan,
  ].filter(Boolean);
  lines.forEach((line, i) => doc.text(line, headerX, 33 + i * 4.5));
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.8);
  doc.line(margin, 50, pageW - margin, 50);

  // Titre et numéro.
  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("CERTIFICAT DE SCOLARITÉ", pageW / 2, 72, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`N° ${certificate.number}`, pageW / 2, 80, { align: "center" });

  // Corps.
  const female = certificate.gender === "F";
  const signatory = certificate.signatoryName?.trim();
  const title = certificate.signatoryTitle?.trim() || "Le Directeur";
  const intro = signatory
    ? `Je soussigné(e), ${signatory}, ${title.charAt(0).toLowerCase()}${title.slice(1)} de l'établissement ${school.name},`
    : `${title} de l'établissement ${school.name},`;
  const born =
    certificate.birthDate || certificate.birthPlace
      ? `, ${female ? "née" : "né"}${certificate.birthDate ? ` le ${pdfDate(certificate.birthDate)}` : ""}${
          certificate.birthPlace ? ` à ${certificate.birthPlace}` : ""
        }`
      : "";
  const paragraphs = [
    `${intro} certifie que l'élève ${certificate.studentName}${born}, matricule ${certificate.matricule}, est ${
      female ? "régulièrement inscrite" : "régulièrement inscrit"
    } dans notre établissement en classe de ${certificate.className} pour l'année scolaire ${certificate.schoolYear}.`,
    "En foi de quoi, le présent certificat lui est délivré pour servir et valoir ce que de droit.",
  ];
  doc.setTextColor(...TEXT);
  doc.setFontSize(12);
  let y = 100;
  for (const paragraph of paragraphs) {
    const wrapped = doc.splitTextToSize(paragraph, width) as string[];
    doc.text(wrapped, margin, y, { lineHeightFactor: 1.7 });
    y += wrapped.length * 12 * 0.3528 * 1.7 + 8;
  }

  // Lieu, date et signature.
  y += 8;
  doc.setFontSize(11);
  doc.text(
    `Fait à ${school.city || "…………………"}, le ${pdfDate(certificate.createdAt)}`,
    pageW - margin,
    y,
    { align: "right" },
  );
  doc.setFont("helvetica", "bold");
  doc.text(title, pageW - margin, y + 12, { align: "right" });
  if (signatory) {
    doc.setFont("helvetica", "normal");
    doc.text(signatory, pageW - margin, y + 45, { align: "right" });
  }
  doc.setDrawColor(190, 190, 185);
  doc.setLineWidth(0.2);
  doc.rect(pageW - margin - 70, y + 16, 70, 24, "S");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text("Signature et cachet", pageW - margin - 35, y + 38, { align: "center" });

  // Pied de page.
  doc.setFontSize(8);
  doc.text(
    `Certificat N° ${certificate.number} délivré le ${pdfDate(certificate.createdAt)} par ${certificate.issuedByName}.`,
    pageW / 2,
    285,
    { align: "center" },
  );

  doc.save(`${certificate.number}.pdf`);
}
